/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarCheck } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type IntakeView } from '../../lib/api/intake.js';
import { useTherapists } from '../../lib/queries/clinical.js';
import { useMarkIntakeArrived, useScheduleIntake } from '../../lib/queries/intake.js';

import { INTAKE_CARD_CLASS } from './intake-form-utils.js';

const FIELD_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** Admin: assign a therapist + book the intake appointment (creates a real session). */
export function IntakeSchedulePanel({ view }: { readonly view: IntakeView }): JSX.Element {
  const { t } = useTranslation();
  const therapists = useTherapists();
  const schedule = useScheduleIntake(view.clientId);
  const arrive = useMarkIntakeArrived(view.clientId);
  const [therapistId, setTherapistId] = useState('');
  const [when, setWhen] = useState('');

  const onArrive = useCallback(() => {
    arrive.mutate();
  }, [arrive]);

  const therapistName = useMemo(
    () => (therapists.data ?? []).find((x) => x.id === view.assignedTherapistId)?.fullName ?? null,
    [therapists.data, view.assignedTherapistId],
  );

  const onTherapist = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTherapistId(e.target.value);
  }, []);
  const onWhen = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setWhen(e.target.value);
  }, []);
  const onSchedule = useCallback(() => {
    if (therapistId === '' || when === '') {
      return;
    }
    schedule.mutate({ therapistId, scheduledStart: new Date(when).toISOString() });
  }, [schedule, therapistId, when]);

  if (view.scheduledSessionId !== null) {
    return (
      <div className={INTAKE_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
          <CalendarCheck className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {therapistName !== null
              ? t('intake.schedule.scheduledWith', { name: therapistName })
              : t('intake.schedule.booked')}
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-400">{t('intake.schedule.inCalendar')}</p>
        {view.status === 'arrived' ? (
          <p className="mt-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            {t('intake.schedule.arrivedNote')}
          </p>
        ) : (
          <Button type="button" className="mt-3" onClick={onArrive} disabled={arrive.isPending}>
            {t('intake.schedule.markArrived')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={INTAKE_CARD_CLASS}>
      <h3 className="mb-4 text-base font-semibold text-slate-800 dark:text-white">
        {t('intake.schedule.title')}
      </h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="intake-therapist" className={LABEL_CLASS}>
            {t('intake.schedule.therapist')}
          </label>
          <select
            id="intake-therapist"
            className={FIELD_CLASS}
            value={therapistId}
            onChange={onTherapist}
          >
            <option value="" disabled>
              {t('intake.schedule.selectTherapist')}
            </option>
            {(therapists.data ?? []).map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="intake-when" className={LABEL_CLASS}>
            {t('intake.schedule.dateTime')}
          </label>
          <input
            id="intake-when"
            type="datetime-local"
            className={FIELD_CLASS}
            value={when}
            onChange={onWhen}
          />
        </div>
      </div>
      {schedule.isError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          onClick={onSchedule}
          disabled={therapistId === '' || when === '' || schedule.isPending}
        >
          {t('intake.schedule.book')}
        </Button>
      </div>
    </div>
  );
}
